import React, { useEffect, useMemo, useState } from "react";
import moment from "moment-timezone";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { drawerVariants } from "../../assets/framer/styles";
import {
  setTimezone,
  setStartDate,
  setEndDate,
  setHourOfDonation,
  setInterval
} from "../../store/slices/donation";
import {
  navbarVariants,
  logoVariants,
  dropdownVariants
} from "../../assets/framer/styles";
import "./index.scss";
import logo from "../../assets/images/barakah/logo.png";
import CustomDropdown from "../../components/CustomDropdown";
import { FaBars, FaTimes } from "react-icons/fa";
// Import all calendars including the new ones:
import {
  rajabFirst10Days,
  fridays,
  ramadanHijri,
  ramadanLunar,
  ramadanHijri10,
  ramadanLunar10,
  test
} from "../../utils/calendarTemplates";
import { useTranslation } from "react-i18next";

const Navbar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { i18n } = useTranslation();

  const currentTimeZone = useSelector((state) => state.donation.timezone);
  const [selectedTimezoneName, setSelectedTimezoneName] = useState(null);
  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const searchParams = new URLSearchParams(location.search);
  const template = searchParams.get("template");

  // All calendars – add the new ones to the list
  const allCalendars = [
    rajabFirst10Days,
    fridays,
    ramadanHijri,
    ramadanLunar,
    ramadanHijri10,
    ramadanLunar10,
    test
  ].map((item, index) => ({
    ...item,
    value: index
  }));

  // Determine if the current template is a "10-night" calendar
  const isTenNightCalendar = template === "Hijri10" || template === "Lunar10";

  // Filter calendar options based on the current template:
  const calendarOptions = allCalendars.filter((cal) => {
    if(template === "Fridays") {
      return cal.label === "Fridays";
    }
    if (isTenNightCalendar) {
      return cal.label === "Hijri10" || cal.label === "Lunar10";
    } else {
      return cal.label === "Hijri" || cal.label === "Lunar";
    }
  });

  // Set timezone options (same as before)
  const timezones = useMemo(() => {
    return moment.tz.names().map((tz) => {
      const offset = moment.tz(tz).utcOffset() / 60;
      const sign = offset >= 0 ? "+" : "";
      const gmtOffset = `(GMT${sign}${offset})`;
      return {
        label: `${tz} ${gmtOffset}`,
        value: tz,
        offset: gmtOffset
      };
    });
  }, []);


  // On initial or URL change, set the selectedCalendar based on template.
  // Note: Only auto-select if the current template is one of the calendar options.
  useEffect(() => {
    if (template) {
      const foundCalendar = calendarOptions.find(
        (calendar) => calendar.label.toLowerCase() === template.toLowerCase()
      );
      if (foundCalendar) {
        setSelectedCalendar(foundCalendar.value);
      }
    }
  }, [template, calendarOptions]);

  // Set timezone and language once
  useEffect(() => {
    const detectedTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    dispatch(setTimezone(detectedTimeZone));
    setSelectedTimezoneName(detectedTimeZone);
  }, [dispatch]);

  const handleTimezoneChange = (selectedOption) => {
    dispatch(setTimezone(selectedOption.value));
    setSelectedTimezoneName(selectedOption.value);
  };

  // Update Redux state when selectedCalendar changes
  useEffect(() => {
    const currentCalendar = allCalendars[selectedCalendar];
    if (currentCalendar) {
      dispatch(setStartDate(currentCalendar.startDate));
      dispatch(setEndDate(currentCalendar.endDate));
      dispatch(setHourOfDonation(currentCalendar.hourOfDonation));
      dispatch(setInterval(currentCalendar.interval));
    }
  }, [selectedCalendar, allCalendars, dispatch]);

  useEffect(() => {
    const currentParams = new URLSearchParams(location.search);
    currentParams.set("template", template);
  }, [template, navigate]);


  const handleCalendarChange = (selectedOption) => {
    // Update Redux state for calendar values
    dispatch(setStartDate(selectedOption.startDate));
    dispatch(setEndDate(selectedOption.endDate));
    dispatch(setHourOfDonation(selectedOption.hourOfDonation));
    dispatch(setInterval(selectedOption.interval));
    setSelectedCalendar(selectedOption.value);

    // Update URL query parameters
    const currentParams = new URLSearchParams(location.search);
    currentParams.set("template", selectedOption.label);
    navigate(`/donate?${currentParams.toString()}`);
  };

  const handleLogoClick = () => {
    navigate("/");
  };

  // Determine if we should show the "Select Timezone" dropdown (only on /donate)
  const shouldShowTimezoneDropdown = location.pathname === "/donate";

  // Determine if we should show the "Select Calendar" dropdown.
  // For this example, we'll show it only if the template is one of our Ramadan calendars.
  const shouldShowCalendarDropdown =
    template === "Hijri" ||
    template === "Lunar" ||
    template === "Hijri10" ||
    template === "Lunar10";

  return (
    <>
      <motion.div
        className="navbar-container"
        variants={navbarVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.img
          className="logo"
          style={{ maxWidth: "250px" }}
          src={logo}
          alt="logo"
          onClick={handleLogoClick}
          variants={logoVariants}
          whileHover="hover"
        />
        <motion.div className="settings-container" variants={navbarVariants}>
          {shouldShowTimezoneDropdown && (
            <motion.div variants={dropdownVariants}>
              <CustomDropdown
                options={timezones}
                selectedOption={timezones.find(
                  (tz) => tz.value === selectedTimezoneName
                )}
                onChange={handleTimezoneChange}
                placeholder="Select Timezone"
                minWidth={270}
              />
            </motion.div>
          )}
          {shouldShowCalendarDropdown && (
            <motion.div variants={dropdownVariants}>
              <CustomDropdown
                options={calendarOptions}
                selectedOption={calendarOptions.find(
                  (calendar) => calendar.value === selectedCalendar
                )}
                onChange={handleCalendarChange}
                placeholder="Select Calendar"
                formatOptionLabel={(option) => option.label.replace(/10$/, "")}
              />
            </motion.div>
          )}
        </motion.div>
      </motion.div>
      <AnimatePresence>
        {drawerOpen && (
          <motion.div
            className="drawer"
            variants={drawerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <button
              className="close-button"
              onClick={() => setDrawerOpen(false)}
            >
              <FaTimes size={24} />
            </button>
            <div className="drawer-content">
              <CustomDropdown
                options={timezones}
                selectedOption={timezones.find(
                  (tz) => tz.value === selectedTimezoneName
                )}
                onChange={handleTimezoneChange}
                placeholder="Select Timezone"
              />
              {shouldShowCalendarDropdown && (
                <motion.div variants={dropdownVariants}>
                  <CustomDropdown
                    options={calendarOptions}
                    selectedOption={calendarOptions.find(
                      (calendar) => calendar.value === selectedCalendar
                    )}
                    onChange={handleCalendarChange}
                    placeholder="Select Calendar"
                  />
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
